import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Branch, Status } from '../utils/types';
import { deleteBranch } from '../services/seller/SellerServices';

interface BranchesDialogProps {
  open: boolean;
  onClose: () => void;
  branches: Branch[];
  jewelerName: string;
  onBranchDeleted?: (deletedBranchId: string) => void;
}

export default function BranchesDialog({ open, onClose, branches, jewelerName, onBranchDeleted }: BranchesDialogProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const getStatusChip = (status: Status) => {
    return (
      <Chip
        label={status === Status.ACTIVE ? 'Aktif' : 'Pasif'}
        color={status === Status.ACTIVE ? 'success' : 'error'}
        size="small"
        variant="outlined"
      />
    );
  };

  const getMainBranchChip = (isMain: boolean) => {
    if (isMain) {
      return (
        <Chip
          label="Ana Şube"
          color="primary"
          size="small"
          variant="filled"
        />
      );
    }
    return null;
  };

  const formatDate = (dateString: string | Date) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteClick = (branch: Branch) => {
    setSelectedBranch(branch);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBranch) return;
    
    setIsDeleting(true);
    try {
      await deleteBranch(selectedBranch.id);
      onBranchDeleted?.(selectedBranch.id);
      setDeleteDialogOpen(false);
      setSelectedBranch(null);
    } catch (error) {
      console.error('Şube silinirken hata oluştu:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedBranch(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {jewelerName} - Şube Listesi
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Toplam {branches.length} şube
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Şube Adı</TableCell>
                <TableCell>Adres</TableCell>
                <TableCell>Telefon</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Ana Şube</TableCell>
                <TableCell>Kayıt Tarihi</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {branches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Bu kuyumcuda henüz şube bulunmuyor
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                branches.map((branch) => (
                  <TableRow key={branch.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {branch.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {branch.address || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {branch.phone || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(branch.status)}
                    </TableCell>
                    <TableCell>
                      {getMainBranchChip(branch.isMain)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(branch.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Şubeyi Sil">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(branch)}
                          disabled={isDeleting}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Kapat
        </Button>
      </DialogActions>

      {/* Silme Onay Dialog'u */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Şubeyi Sil
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            <strong>{selectedBranch?.name}</strong> şubesini silmek istediğinize emin misiniz?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteCancel} 
            variant="outlined"
            disabled={isDeleting}
          >
            İptal
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
